import { User } from "@/types";
import type { RegisterPrefill } from "@/types/auth";
import { API_BASE } from "./config";
import { isTokenExpired } from "@/utils/tokenValidation";


export type QueryOperation = 'list' | 'get' | 'insert' | 'update' | 'delete';

export type FilterOperator = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'in' | 'not_in' | 'is_null' | 'between' | 'or';

export type FilterCondition = {
  eq?: string | number | boolean;
  ne?: string | number | boolean;
  gt?: string | number | boolean;
  gte?: string | number | boolean;
  lt?: string | number | boolean;
  lte?: string | number | boolean;
  like?: string;
  in?: string[] | number[] | boolean[];
  not_in?: string[] | number[] | boolean[];
  is_null?: boolean;
  between?: [string | number, string | number];
  or?: FilterCondition[];
};

export type FilterOptions = {
  [field: string]: FilterCondition;
};

export interface QueryRequest {
  operation: QueryOperation;
  table: string;
  id?: string | number;
  data?: Record<string, string | number | boolean | Date | null | undefined | any[]>;
  fields?: string[];
  filter?: FilterOptions;
  sort?: { [field: string]: 'asc' | 'desc' };
  limit?: number;
  offset?: number;
  include?: string[];
}

export interface QueryResponse<T> {
  success: boolean;
  data?: T[];
  meta?: {
    total?: number;
    limit?: number;
    offset?: number;
    cached?: boolean;
    execution_time?: number;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface SchemaInfo {
  table: string;
  columns: Array<{
    cid: number;
    name: string;
    type: string;
    notnull: number;
    dflt_value: any;
    pk: number;
  }>;
  foreignKeys: Array<{
    id: number;
    seq: number;
    table: string;
    from: string;
    to: string;
    on_update: string;
    on_delete: string;
    match: string;
  }>;
}

function getAuthToken(): string | null {
  const token = localStorage.getItem('token');
  if (!token) {
    return null;
  }
  if (isTokenExpired(token)) {
    localStorage.removeItem('token');
    return null;
  }
  return token;
}

export function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function query<T = unknown>(request: QueryRequest): Promise<QueryResponse<T>> {
  const response = await fetch(`${API_BASE}/query`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export async function manage<T = unknown>(request: Record<string, string | number | boolean | Date | null | undefined>): Promise<QueryResponse<T>> {
  const response = await fetch(`${API_BASE}/manage`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export const easybackClient = {
  list: <T = unknown>(table: string, options: Omit<QueryRequest, 'operation' | 'table'> = {}) =>
    query<T>({ operation: 'list', table, ...options }),

  get: <T = unknown>(table: string, id: string | number, options: Omit<QueryRequest, 'operation' | 'table' | 'id'> = {}) =>
    query<T>({ operation: 'get', table, id, ...options }),

  insert: <T = unknown>(table: string, data: Record<string, string | number | boolean | Date | null | undefined>) =>
    query<T>({ operation: 'insert', table, data }),

  update: <T = unknown>(table: string, id: string | number, data: Record<string, string | number | boolean | Date | null | undefined>) =>
    query<T>({ operation: 'update', table, id, data }),

  delete: <T = unknown>(table: string, id: string | number) =>
    query<T>({ operation: 'delete', table, id }),

  updateMany: <T = unknown>(table: string, filter: FilterOptions, data: Record<string, string | number | boolean | Date | null | undefined>) =>
    query<T>({ operation: 'update', table, filter, data }),

  schemaInfo: (table?: string) =>
    manage<SchemaInfo>({ operation: 'schema_info', table }),

  clearCache: (table?: string) =>
    manage({ operation: 'cache_clear', table }),

  executeSql: (sql: string) =>
    manage({ operation: 'execute_sql', sql }),

  login: async (email: string, password: string):Promise<{success:boolean, data:{user:User, token:string}}> => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error?.message || 'Giriş başarısız');
    }
    return result;
  },

  register: async (name: string, email: string, password: string, role: string, refOverride?: string, adultConfirmed?: boolean):Promise<{success:boolean, data:{user:User, token:string, prefill?:RegisterPrefill}}> => {
    let ref: string | null = refOverride ?? null;
    if (!ref) {
      try {
        const stored = JSON.parse(localStorage.getItem('dk_ref') || 'null');
        if (stored && typeof stored.code === 'string') ref = stored.code.slice(0, 32);
      } catch { /* no ref */ }
    }
    const response = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, role, adultConfirmed: adultConfirmed === true, ...(ref ? { ref } : {}) }),
    });
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error?.message || 'Kayıt başarısız');
    }
    return result;
  },
};
